// NodeMultiselectField.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getEnumOptionColor, getEnumOptionIconKey, getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getResolvedFields } from 'utilities/NodeType';

// CSS
import './NodeMultiselectField.css';

// Components
import Pill from 'components/pill/Pill';
import NodeMultiselectModal from 'node/form/fields/multiselect/NodeMultiselectModal';

function resolveMultiselectField(node, field, fieldName) {
    if (field?.type === 'multiselect') return field;

    const fields = getResolvedFields(node?.type, { includeHidden: true, excludeSecured: true });
    return fields.find((entry) => entry.name === fieldName && entry.type === 'multiselect') || null;
}

const normalizeSelectedValues = (value) => {
    const list = Array.isArray(value) ? value : value == null || value === '' ? [] : [value];
    const seen = new Set();

    return list
        .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry || '').trim()))
        .filter((entry) => {
            if (!entry || seen.has(entry)) return false;
            seen.add(entry);
            return true;
        });
};

const orderSelectedValues = (options, selectedValues) => {
    const selectedSet = new Set(normalizeSelectedValues(selectedValues));
    const ordered = [];

    (Array.isArray(options) ? options : []).forEach((option) => {
        const optionValue = String(option?.value || '').trim();
        if (!optionValue || !selectedSet.has(optionValue)) return;
        ordered.push(optionValue);
        selectedSet.delete(optionValue);
    });

    return [...ordered, ...Array.from(selectedSet)];
};

/**
 * NodeMultiselectField
 *
 * Displays selected multi-select values as colored pills and opens the picker modal.
 * In readOnly / previewMode: pills stay visible; click / picker disabled.
 *
 * @param {object} node - Node object used to resolve field configuration.
 * @param {object} field - Optional field definition for direct option resolution.
 * @param {string} fieldName - Name of the schema field.
 * @param {string} fieldLabel - Display label used by the picker modal.
 * @param {string[]|string} value - Current selected option values.
 * @param {function} onChange - Callback when the selected values change.
 * @param {boolean} [readOnly=false] - Display-only; no picker.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} The rendered button.
 */
const NodeMultiselectField = ({
    node,
    field,
    fieldName,
    fieldLabel,
    value,
    onChange,
    readOnly = false,
    previewMode = false,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const language = useCurrentLanguage();
    const isReadOnly = Boolean(readOnly || previewMode);

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // ----- MEMOS ----- //

    const options = useMemo(() => {
        const fieldDef = resolveMultiselectField(node, field, fieldName);
        return resolveEnumOptionsForField(fieldDef, language);
    }, [field, fieldName, language, node]);

    const selectedValues = useMemo(() => orderSelectedValues(options, value), [options, value]);

    const pills = useMemo(() => {
        return selectedValues.map((selectedValue, index) => ({
            color: getEnumOptionColor(options, selectedValue),
            iconKey: getEnumOptionIconKey(options, selectedValue),
            key: `${selectedValue}-${index}`,
            label: getEnumOptionLabel(options, selectedValue, language) || selectedValue,
        }));
    }, [language, options, selectedValues]);

    // ----- HANDLERS ----- //

    const handleOpen = () => {
        if (isReadOnly) return;
        setModalOpen(true);
    };

    const handleKeyDown = (event) => {
        if (isReadOnly) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="node-multiselect-field-container">

                <div
                  className={`node-multiselect-field-input${isReadOnly ? ' is-readonly' : ''}`}
                  style={{
                        backgroundColor: (!isReadOnly && hovered) ? theme.backgroundMuted : 'transparent',
                        cursor: isReadOnly ? 'default' : undefined,
                    }}
                  role={isReadOnly ? undefined : 'button'}
                  tabIndex={isReadOnly ? undefined : 0}
                  onClick={isReadOnly ? undefined : handleOpen}
                  onKeyDown={isReadOnly ? undefined : handleKeyDown}
                  onMouseEnter={isReadOnly ? undefined : () => setHovered(true)}
                  onMouseLeave={isReadOnly ? undefined : () => setHovered(false)}
                >
                    {pills.length === 0 ? (
                        <div className="node-multiselect-field-empty">
                            {isReadOnly ? '' : 'Select options...'}
                        </div>
                    ) : (
                        <div className="node-multiselect-field-pills">
                            {pills.map((pill) => (
                                <Pill
                                  key={pill.key}
                                  color={pill.color || theme.panel}
                                  iconKey={pill.iconKey}
                                  label={pill.label}
                                  labelColor={theme.highlightForeground}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {!isReadOnly ? (
                <NodeMultiselectModal
                  node={node}
                  field={field}
                  fieldName={fieldName}
                  label={fieldLabel}
                  value={selectedValues}
                  isOpen={modalOpen}
                  setOpen={setModalOpen}
                  onChange={onChange}
                />
            ) : null}
        </>
    );
};

// Dev Tools
NodeMultiselectField.displayName = 'NodeMultiselectField';

export default NodeMultiselectField;
